import type { Gear } from '../../sim/types';

/**
 * Palanca en H interactiva: muestra dónde está la marcha engranada y permite
 * pedir otra tocando/clicando su puerta (el simulador exigirá el embrague).
 */

interface GatePos {
  gear: Gear;
  x: number;
  y: number;
}

const TOP_Y = 15;
const BOT_Y = 57;
const MID_Y = 36;

function layoutGates(gearCount: number): { gates: GatePos[]; width: number; nx: number } {
  const cols = Math.ceil(gearCount / 2) + 1; // columnas de marchas + columna R
  const step = 26;
  const x0 = 16;
  const gates: GatePos[] = [];
  for (let g = 1; g <= gearCount; g++) {
    const col = Math.floor((g - 1) / 2);
    gates.push({ gear: g as Gear, x: x0 + col * step, y: g % 2 === 1 ? TOP_Y : BOT_Y });
  }
  const rx = x0 + (cols - 1) * step;
  gates.push({ gear: 'R', x: rx, y: BOT_Y });
  const width = rx + 16;
  const nx = (x0 + rx) / 2;
  return { gates, width, nx };
}

export function HShifter({
  gearCount,
  current,
  requested,
  onSelect,
}: {
  gearCount: number;
  current: Gear;
  requested: Gear;
  onSelect: (g: Gear) => void;
}) {
  const { gates, width, nx } = layoutGates(gearCount);
  const posOf = (g: Gear): [number, number] => {
    if (g === 'N' || g === 'P' || g === 'D') return [nx, MID_Y];
    const gate = gates.find((it) => it.gear === g);
    return gate ? [gate.x, gate.y] : [nx, MID_Y];
  };
  const [cx, cy] = posOf(current);
  const [qx, qy] = posOf(requested);
  const pendingElsewhere = requested !== current && (qx !== cx || qy !== cy);

  return (
    <svg
      viewBox={`0 0 ${width} 72`}
      className="pointer-events-auto h-[4.6rem] select-none"
      style={{ width: `${width * 1.02}px` }}
    >
      {/* Canales de la H */}
      {Array.from(new Set(gates.map((g) => g.x))).map((x) => (
        <line key={`v${x}`} x1={x} y1={TOP_Y} x2={x} y2={BOT_Y} stroke="rgba(255,255,255,0.22)" strokeWidth="7" strokeLinecap="round" />
      ))}
      <line x1={gates[0]!.x} y1={MID_Y} x2={gates[gates.length - 1]!.x} y2={MID_Y} stroke="rgba(255,255,255,0.22)" strokeWidth="7" strokeLinecap="round" />

      {/* Puertas + etiquetas */}
      {gates.map((g) => (
        <g key={String(g.gear)} className="cursor-pointer" onPointerDown={(e) => { e.preventDefault(); onSelect(g.gear); }}>
          <circle cx={g.x} cy={g.y} r="11" fill="rgba(255,255,255,0.001)" />
          <text
            x={g.x}
            y={g.y === TOP_Y ? g.y - 8.5 : g.y + 13.5}
            textAnchor="middle"
            fontSize="9.5"
            fontWeight="800"
            fill={g.gear === 'R' ? '#fcd34d' : 'rgba(255,255,255,0.85)'}
          >
            {String(g.gear)}
          </text>
        </g>
      ))}
      {/* Punto muerto (N): zona central */}
      <g className="cursor-pointer" onPointerDown={(e) => { e.preventDefault(); onSelect('N'); }}>
        <circle cx={nx} cy={MID_Y} r="10" fill="rgba(255,255,255,0.001)" />
        <text x={nx} y={MID_Y - 9} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="rgba(255,255,255,0.5)">
          N
        </text>
      </g>

      {/* Marcha solicitada aún no engranada */}
      {pendingElsewhere && (
        <circle cx={qx} cy={qy} r="8" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.6" strokeDasharray="3 2.4">
          <animate attributeName="opacity" values="1;0.25;1" dur="0.7s" repeatCount="indefinite" />
        </circle>
      )}
      {/* Pomo en la marcha engranada */}
      <circle cx={cx} cy={cy} r="7.4" fill={current === 'R' ? '#fbbf24' : '#38bdf8'} stroke="#020617" strokeWidth="2" />
    </svg>
  );
}
